import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Users, Trophy, Plus, X, Upload, Shield, ImagePlus, Loader2, Eye } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Switch } from '../ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveBadgeUrl = (badge) => {
  if (!badge) return null;
  if (badge.startsWith('http://') || badge.startsWith('https://')) return badge;
  return `${BACKEND_URL}${badge}`;
};

export const OpponentsCompetitionsSection = ({ showOpponentBadges, setShowOpponentBadges }) => {
  const [opponents, setOpponents] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [newOpponent, setNewOpponent] = useState('');
  const [newCompetition, setNewCompetition] = useState('');
  const [bulkOpponents, setBulkOpponents] = useState('');
  const [bulkCompetitions, setBulkCompetitions] = useState('');
  const [showBulkOpponents, setShowBulkOpponents] = useState(false);
  const [showBulkCompetitions, setShowBulkCompetitions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputRefs = useRef({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [opponentsRes, competitionsRes] = await Promise.all([
        axios.get(`${API}/opponents`),
        axios.get(`${API}/competitions`)
      ]);
      setOpponents(opponentsRes.data);
      setCompetitions(competitionsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Opponents handlers
  const addOpponent = async () => {
    if (!newOpponent.trim()) return;
    try {
      const res = await axios.post(`${API}/opponents`, { name: newOpponent.trim() });
      setOpponents([...opponents, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewOpponent('');
      toast.success('Opponent added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add opponent');
    }
  };

  const addBulkOpponents = async () => {
    const names = bulkOpponents.split('\n').map(n => n.trim()).filter(n => n);
    if (names.length === 0) return;
    try {
      const res = await axios.post(`${API}/opponents/bulk`, { names });
      if (res.data.length > 0) {
        setOpponents([...opponents, ...res.data].sort((a, b) => a.name.localeCompare(b.name)));
        toast.success(`Added ${res.data.length} opponent(s)`);
      } else {
        toast.info('No new opponents added (all may already exist)');
      }
      setBulkOpponents('');
      setShowBulkOpponents(false);
    } catch (error) {
      toast.error('Failed to add opponents');
    }
  };

  const deleteOpponent = async (id) => {
    try {
      await axios.delete(`${API}/opponents/${id}`);
      setOpponents(opponents.filter(o => o.id !== id));
      toast.success('Opponent removed');
    } catch (error) {
      toast.error('Failed to remove opponent');
    }
  };

  const uploadBadge = async (opponent, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    setUploadingId(opponent.id);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await axios.post(`${API}/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      const badgeUrl = uploadRes.data.image_url;
      const updateRes = await axios.put(`${API}/opponents/${opponent.id}`, {
        name: opponent.name,
        badge: badgeUrl
      });
      setOpponents(opponents.map(o => o.id === opponent.id ? updateRes.data : o));
      toast.success('Badge updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload badge');
    } finally {
      setUploadingId(null);
      if (fileInputRefs.current[opponent.id]) {
        fileInputRefs.current[opponent.id].value = '';
      }
    }
  };

  const removeBadge = async (opponent) => {
    try {
      const updateRes = await axios.put(`${API}/opponents/${opponent.id}`, {
        name: opponent.name,
        badge: ''
      });
      setOpponents(opponents.map(o => o.id === opponent.id ? updateRes.data : o));
      toast.success('Badge removed');
    } catch (error) {
      toast.error('Failed to remove badge');
    }
  };

  // Competitions handlers
  const addCompetition = async () => {
    if (!newCompetition.trim()) return;
    try {
      const res = await axios.post(`${API}/competitions`, { name: newCompetition.trim() });
      setCompetitions([...competitions, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewCompetition('');
      toast.success('Competition added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add competition');
    }
  };

  const addBulkCompetitions = async () => {
    const names = bulkCompetitions.split('\n').map(n => n.trim()).filter(n => n);
    if (names.length === 0) return;
    try {
      const res = await axios.post(`${API}/competitions/bulk`, { names });
      if (res.data.length > 0) {
        setCompetitions([...competitions, ...res.data].sort((a, b) => a.name.localeCompare(b.name)));
        toast.success(`Added ${res.data.length} competition(s)`);
      } else {
        toast.info('No new competitions added (all may already exist)');
      }
      setBulkCompetitions('');
      setShowBulkCompetitions(false);
    } catch (error) {
      toast.error('Failed to add competitions');
    }
  };

  const deleteCompetition = async (id) => {
    try {
      await axios.delete(`${API}/competitions/${id}`);
      setCompetitions(competitions.filter(c => c.id !== id));
      toast.success('Competition removed');
    } catch (error) {
      toast.error('Failed to remove competition');
    }
  };

  if (loading) {
    return (
      <Card data-testid="opponents-competitions-card">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-200 rounded w-1/3"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="opponents-competitions-card">
      <CardHeader>
        <CardTitle className="text-xl font-semibold font-title flex items-center gap-2">
          <Users className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
          Opponents & Competitions
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Opponents Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-base font-medium flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-500" />
              Opponents ({opponents.length})
            </Label>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowBulkOpponents(!showBulkOpponents)}
              className="text-emerald-600 hover:text-emerald-700"
            >
              <Upload className="w-4 h-4 mr-1" />
              Bulk Add
            </Button>
          </div>
          
          {/* Add single opponent */}
          <div className="flex gap-2">
            <Input
              placeholder="Enter opponent name..."
              value={newOpponent}
              onChange={(e) => setNewOpponent(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addOpponent()}
              className="rounded-xl"
              data-testid="new-opponent-input"
            />
            <Button
              onClick={addOpponent}
              disabled={!newOpponent.trim()}
              className="rounded-xl"
              data-testid="add-opponent-btn"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          {/* Bulk add opponents */}
          {showBulkOpponents && (
            <div className="p-3 bg-gray-50 rounded-xl space-y-2">
              <Label className="text-sm text-gray-600">Add multiple opponents (one per line)</Label>
              <Textarea
                placeholder="Team A&#10;Team B&#10;Team C"
                value={bulkOpponents}
                onChange={(e) => setBulkOpponents(e.target.value)}
                rows={4}
                className="rounded-xl"
                data-testid="bulk-opponents-textarea"
              />
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" size="sm" onClick={() => setShowBulkOpponents(false)}>
                  Cancel
                </Button>
                <Button size="sm" onClick={addBulkOpponents} data-testid="bulk-add-opponents-btn">
                  Add All
                </Button>
              </div>
            </div>
          )}

          {/* Opponents list */}
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {opponents.map((opponent) => {
              const badgeUrl = resolveBadgeUrl(opponent.badge);
              const isUploading = uploadingId === opponent.id;
              return (
                <div
                  key={opponent.id}
                  className="flex items-center gap-3 p-2 pl-3 bg-gray-50 hover:bg-gray-100 rounded-xl border border-gray-200 transition-colors"
                  data-testid={`opponent-row-${opponent.id}`}
                >
                  {/* Badge preview */}
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center overflow-hidden">
                    {badgeUrl ? (
                      <img
                        src={badgeUrl}
                        alt={`${opponent.name} badge`}
                        className="w-full h-full object-cover"
                        data-testid={`opponent-badge-img-${opponent.id}`}
                      />
                    ) : (
                      <Shield className="w-5 h-5 text-gray-300" strokeWidth={1.5} />
                    )}
                  </div>

                  {/* Name */}
                  <span className="flex-1 text-sm font-medium text-gray-800 truncate">
                    {opponent.name}
                  </span>

                  {/* Hidden file input */}
                  <input
                    type="file"
                    accept="image/*"
                    ref={(el) => { fileInputRefs.current[opponent.id] = el; }}
                    onChange={(e) => uploadBadge(opponent, e.target.files?.[0])}
                    className="hidden"
                    data-testid={`opponent-badge-file-input-${opponent.id}`}
                  />

                  {/* Upload / Change badge */}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={isUploading}
                    onClick={() => fileInputRefs.current[opponent.id]?.click()}
                    className="h-8 px-2 text-xs text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                    data-testid={`opponent-badge-upload-btn-${opponent.id}`}
                  >
                    {isUploading ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <ImagePlus className="w-3.5 h-3.5" />
                    )}
                    <span className="ml-1 hidden sm:inline">
                      {opponent.badge ? 'Change' : 'Badge'}
                    </span>
                  </Button>

                  {/* Remove badge (only when present) */}
                  {opponent.badge && !isUploading && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeBadge(opponent)}
                      className="h-8 w-8 p-0 text-gray-400 hover:text-red-500"
                      data-testid={`opponent-badge-remove-btn-${opponent.id}`}
                      title="Remove badge"
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  )}

                  {/* Delete opponent */}
                  <button
                    type="button"
                    onClick={() => deleteOpponent(opponent.id)}
                    className="flex-shrink-0 p-1 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                    data-testid={`delete-opponent-${opponent.id}`}
                    title="Delete opponent"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
            {opponents.length === 0 && (
              <p className="text-sm text-gray-400 italic">No opponents added yet</p>
            )}
          </div>

          {/* Show opponent badges toggle */}
          {typeof setShowOpponentBadges === 'function' && (
            <div className="flex items-center justify-between gap-3 p-3 bg-emerald-50/50 rounded-xl border border-emerald-100">
              <div className="flex items-start gap-2 min-w-0">
                <Eye className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                <div className="min-w-0">
                  <Label className="text-sm font-medium text-gray-800">Show opponent badges on matches</Label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Display each opponent's badge on match cards. When off, cards align to the left.
                  </p>
                </div>
              </div>
              <Switch
                checked={!!showOpponentBadges}
                onCheckedChange={setShowOpponentBadges}
                data-testid="show-opponent-badges-toggle"
              />
            </div>
          )}
        </div>

        <div className="border-t border-gray-200"></div>

        {/* Competitions Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-base font-medium flex items-center gap-2">
              <Trophy className="w-4 h-4 text-gray-500" />
              Competitions ({competitions.length})
            </Label>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowBulkCompetitions(!showBulkCompetitions)}
              className="text-emerald-600 hover:text-emerald-700"
            >
              <Upload className="w-4 h-4 mr-1" />
              Bulk Add
            </Button>
          </div>
          
          {/* Add single competition */}
          <div className="flex gap-2">
            <Input
              placeholder="Enter competition name..."
              value={newCompetition}
              onChange={(e) => setNewCompetition(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addCompetition()}
              className="rounded-xl"
              data-testid="new-competition-input"
            />
            <Button
              onClick={addCompetition}
              disabled={!newCompetition.trim()}
              className="rounded-xl"
              data-testid="add-competition-btn"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          {/* Bulk add competitions */}
          {showBulkCompetitions && (
            <div className="p-3 bg-gray-50 rounded-xl space-y-2">
              <Label className="text-sm text-gray-600">Add multiple competitions (one per line)</Label>
              <Textarea
                placeholder="Premier League&#10;FA Cup&#10;League Cup"
                value={bulkCompetitions}
                onChange={(e) => setBulkCompetitions(e.target.value)}
                rows={4}
                className="rounded-xl"
                data-testid="bulk-competitions-textarea"
              />
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" size="sm" onClick={() => setShowBulkCompetitions(false)}>
                  Cancel
                </Button>
                <Button size="sm" onClick={addBulkCompetitions} data-testid="bulk-add-competitions-btn">
                  Add All
                </Button>
              </div>
            </div>
          )}

          {/* Competitions list */}
          <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
            {competitions.map((competition) => (
              <div
                key={competition.id}
                className="flex items-center gap-1 px-3 py-1.5 bg-amber-100 rounded-full text-sm group"
              >
                <span>{competition.name}</span>
                <button
                  onClick={() => deleteCompetition(competition.id)}
                  className="ml-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`delete-competition-${competition.id}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {competitions.length === 0 && (
              <p className="text-sm text-gray-400 italic">No competitions added yet</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default OpponentsCompetitionsSection;
